import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { TtIcon } from '@23/react'

import useFeatures from '../../../../hooks/useFeatures'
import useSearchParams from '../../../../hooks/useSearchParams'

// @ts-expect-error — importing js
import ButtonComponent from '../../../../components/ButtonComponent/ButtonComponent'
import JoinToSpeakInfoPanel from '../../../../../Webinar/components/JoinToSpeakInfoPanel/JoinToSpeakInfoPanel'
import JoinToSpeakNotSupportedPanel from '../../../../../Webinar/components/JoinToSpeakNotSupportedPanel/JoinToSpeakNotSupportedPanel'
import JoinToSpeakAccess from '../WebinarAudienceSidebarComponent/JoinToSpeak/JoinToSpeakAccess'
import { WebinarQuestionsCardComponent } from '../index'
import { JoinToSpeakIDSearchParamName } from '../../../../join-to-speak'

import './WebinarInfoAudienceSidebarComponent.scss'
import { WebinarSidebarSeparator } from '../../../WebinarSidebarFeature/components'
import { initializeDevicesAsAudience } from '../../../../../Devices/useDevices'

interface WebinarInfoAudienceSidebarProps {
  liveData: Record<string, any>
  isQuestionsEnabled: boolean
  onAskQuestion: (a: any, b: any) => void // TODO: type this
  localParticipantName: string // TODO: remove this
  onLocalParticipantNameUpdated: Function // TODO: remove this
  styles: any // TODO: remove
  isMobileDevice: boolean
}

const STORAGE_KEY_JOIN_TO_SPEAK_INFO = 'tt:j2s-info-seen'

const WebinarInfoAudienceSidebarComponent = ({
  liveData,
  isQuestionsEnabled,
  onAskQuestion,
  localParticipantName,
  onLocalParticipantNameUpdated,
  styles,
  isMobileDevice = false,
}: WebinarInfoAudienceSidebarProps) => {
  const { HasJoinToSpeakEnabled } = useFeatures()
  const joinToSpeakID = useSearchParams().get(JoinToSpeakIDSearchParamName)

  const canJoinToSpeak = useMemo(() => {
    return HasJoinToSpeakEnabled && Boolean(liveData.allow_guest_speakers_p)
  }, [HasJoinToSpeakEnabled, liveData.allow_guest_speakers_p])

  const [currentTopPanel, setCurrentTopPanel] = useState<
    'oncamera' | 'question' | 'oncamera-intro' | 'oncamera-unsupported'
  >(joinToSpeakID ? 'oncamera' : 'question')

  useEffect(() => {
    if (joinToSpeakID) {
      initializeDevicesAsAudience()
    }
  }, [joinToSpeakID])

  const handleOnCameraJoinClick = useCallback(async () => {
    const hasAlreadySeenInfo = JSON.parse(
      localStorage.getItem(STORAGE_KEY_JOIN_TO_SPEAK_INFO) ?? 'false'
    )

    if (!hasAlreadySeenInfo) {
      setCurrentTopPanel('oncamera-intro')
      return
    }

    // request permissions
    const areDevicesInitialized = await initializeDevicesAsAudience()

    if (!areDevicesInitialized) {
      setCurrentTopPanel('oncamera-intro')
      return
    }

    if (!window.isSecureContext) {
      setCurrentTopPanel('oncamera-unsupported')
      return
    }

    setCurrentTopPanel('oncamera')
  }, [])

  if (isMobileDevice) {
    if (!isQuestionsEnabled) return null
    return (
      <WebinarQuestionsCardComponent
        onAskQuestion={onAskQuestion}
        styles={styles}
        localParticipantName={localParticipantName}
        onLocalParticipantNameUpdated={onLocalParticipantNameUpdated}
        allowAnonymous={false}
      />
    )
  }

  if (currentTopPanel === 'question') {
    return (
      <>
        {canJoinToSpeak && liveData.streaming_method !== 'hardware' && (
          <ButtonComponent
            className="go-on-camera-button"
            color="themed"
            ariaLabel="Ask your question on camera"
            onClick={handleOnCameraJoinClick}
            round
            disabled={localParticipantName === undefined}
          >
            <TtIcon
              name="record"
              library="23"
              className="go-on-camera-button-icon"
            />
            <FormattedMessage defaultMessage="Go on camera" />
          </ButtonComponent>
        )}
        {isQuestionsEnabled && (
          <WebinarQuestionsCardComponent
            onAskQuestion={onAskQuestion}
            styles={styles}
            localParticipantName={localParticipantName}
            onLocalParticipantNameUpdated={onLocalParticipantNameUpdated}
            allowAnonymous={false}
          />
        )}
      </>
    )
  }

  if (!canJoinToSpeak) return null

  switch (currentTopPanel) {
    case 'oncamera':
      return (
        <>
          <JoinToSpeakAccess
            sessionId={liveData.live_id}
            onClose={() => setCurrentTopPanel('question')}
          />
          <WebinarSidebarSeparator />
        </>
      )
    case 'oncamera-intro':
      return (
        <JoinToSpeakInfoPanel
          onDismiss={() => {
            setCurrentTopPanel('question')
          }}
          onAccept={async () => {
            const areDevicesInitialized = await initializeDevicesAsAudience()

            if (!areDevicesInitialized) return

            if (!window.isSecureContext) {
              setCurrentTopPanel('oncamera-unsupported')
              return
            }

            localStorage[STORAGE_KEY_JOIN_TO_SPEAK_INFO] = JSON.stringify(true)
            setCurrentTopPanel('oncamera')
          }}
        />
      )
    case 'oncamera-unsupported':
      return (
        <JoinToSpeakNotSupportedPanel
          onDismiss={() => setCurrentTopPanel('question')}
        />
      )
    default:
  }

  return <></>
}

export default WebinarInfoAudienceSidebarComponent
